import { useCrossTabPaidolSync } from 'app/auth/useCrossTabPaidolSync';
import { Role } from 'API';
import { useAppDispatch, useAppSelector } from 'app/store';
import { getUserProfile } from 'app/store/userProfileSlice';
import { Auth, Logger } from 'aws-amplify';
import { createContext, ReactNode, useContext, useEffect, useRef } from 'react';
import { useLogger } from 'util/logging';
import { getPendingSsoAttempt, resolveSsoAttempt } from 'app/auth/ssoAttemptLog';
import {
  authenticatedDisplayIdentity,
  extractSsoIdentifiers,
  matchesTypedIdentifier,
  type SsoIdTokenClaims,
} from 'app/auth/ssoAccountMatch';
import { clearSsoSwitchMarker, logSsoMismatch, readSsoSwitchMarker } from 'app/auth/ssoAccountVerification';
import {
  AuthState,
  getCurrentUser,
  handleFederatedSession,
  initialState as initialAuthState,
  PAIDOL_SESSION_ID_KEY,
  savePaidolSessionId,
  selectAuth,
  SESSION_ID_KEY,
  setSsoMismatch,
  triggerBackupLocalStorage,
  paidolIdFromGroups,
} from 'app/store/authSlice';
import {
  getUserCompanies,
  hydrateSelectedPaidolIfNeeded,
  initialState as initialUserCompaniesState,
  selectUserCompanies,
  UserCompaniesState,
} from 'app/store/userCompaniesSlice';

export interface AuthContextData {
  authState: AuthState;
  userCompaniesState: UserCompaniesState;
}

export const AuthContext = createContext<AuthContextData>({
  authState: initialAuthState,
  userCompaniesState: initialUserCompaniesState,
});

interface AuthProviderProps {
  children: ReactNode;
}

function AuthProvider({ children }: AuthProviderProps): JSX.Element {
  useCrossTabPaidolSync();
  const dispatch = useAppDispatch();
  const authState = useAppSelector(selectAuth);
  const userCompaniesState = useAppSelector(selectUserCompanies);
  const {
    isUserCompaniesStateUnknown,
    selectedPaidol,
    selectedPaidolId,
    user,
    paidolUsers,
    supportPaidolList,
  } = userCompaniesState;
  const userSub = authState.user?.sub;

  const hasSetPaidolId = useRef<boolean>(false);
  const previousSelectedPaidolId = useRef<string>('');
  const hasFetchedUserCompanies = useRef<string | null>(null);
  const hasCalledGetCurrentUser = useRef<boolean>(false);

  // Guard paidol session setup to only run once per userSub
  useEffect(() => {
    async function setupSession() {
      if (!user || hasSetPaidolId.current) return;
      // Wait for getUserCompanies to resolve, but proceed even with empty
      // arrays — support users with no PaidolUser memberships and no firehose
      // (post-SEE-862) need this branch to run so we fall back to
      // cognito:groups[0]; otherwise they'd be stuck on the login screen.
      if (isUserCompaniesStateUnknown) return;
      const currentPaidolSessionId = localStorage.getItem(PAIDOL_SESSION_ID_KEY);
      let paidolId = currentPaidolSessionId?.split(':')[0];
      // is_support users can legitimately be on any paidol — the backend's
      // _user_belongs_to_paidol has an is_support bypass, so a client-side
      // preflight that rejects "not in paidolUsers" would falsely invalidate
      // a perfectly accessible paidol and strand the user with no fallback
      // (cognito:groups in doug all look like `us-east-2_…_sso-…` and get
      // filtered out). Trust whatever's in localStorage; the hydrate effect
      // recovers below if the paidol was deleted/inaccessible.
      const isSupport = Boolean(user?.is_support);
      const isValidPaidolId =
        (!!paidolId && isSupport) ||
        paidolUsers.some((paidolUser) => paidolUser.paidol?.id === paidolId) ||
        supportPaidolList.some((paidol) => paidol.id === paidolId);
      if (!isValidPaidolId) {
        try {
          const session = await Auth.currentSession();
          const cognitoGroups = session.getIdToken().payload['cognito:groups'] as string[];
          paidolId = paidolIdFromGroups(cognitoGroups) || cognitoGroups?.[0];
        } catch (error) {
          console.error('Error getting current session:', error);
          return;
        }
        // Last-resort fallback: if localStorage is empty AND cognito:groups
        // didn't yield a usable paidolId (e.g. doug-env groups are all
        // `us-east-2_…_sso-…` and get filtered out, or the user has no
        // groups at all), land on the first PaidolUser membership. Without
        // this fallback the setupSession effect returns without dispatching,
        // selectedPaidolId stays '', and RequireAuth keeps the loading
        // overlay up forever — no way for the user to recover.
        if (!paidolId) {
          // eslint-disable-next-line no-restricted-syntax -- SEE-1352: no session and no usable cognito:groups, so there is no "selected" company to scope to. Any membership is a valid landing spot and beats a permanent spinner; the switcher lets the user correct it.
          paidolId = paidolUsers[0]?.paidol?.id;
        }
      }
      if (paidolId && paidolId !== selectedPaidolId && paidolId !== previousSelectedPaidolId.current) {
        hasSetPaidolId.current = true;
        previousSelectedPaidolId.current = paidolId;
        // The thunk does not reload (SPE-1474), so this is safe to dispatch
        // during initial auth bootstrap. It also writes PAIDOL_SESSION_ID_KEY
        // to localStorage, which middleware's 421 recovery path needs.
        dispatch(savePaidolSessionId(paidolId));
      } else if (paidolId) {
        hasSetPaidolId.current = true;
        previousSelectedPaidolId.current = paidolId;
      }
    }
    setupSession();
  }, [user, paidolUsers, supportPaidolList, isUserCompaniesStateUnknown, selectedPaidolId]);

  // Support users who arrive at a paidol they don't have a PaidolUser row for
  // (e.g. a fresh page load after picking from search) won't get
  // selectedPaidol hydrated through the usual paidolUsers fallback. Pull the
  // single paidol's data via `getPaidolForSwitcher` (graphql_custom), and remember
  // the last attempted id so a null response (deleted/inaccessible paidol)
  // can't loop the dispatch — the fulfilled reducer no-ops on null payload,
  // which would otherwise leave `!selectedPaidol` true forever.
  //
  // Mismatch case: an is_support user chain-switching from non-member paidol
  // A to non-member paidol B can transiently leave selectedPaidolId=B with
  // selectedPaidol still pointing at A (the setSelectedPaidolId reducer's
  // else branch can't always fill selectedPaidol from supportSearchResults).
  // Triggering on id mismatch — not just `!selectedPaidol` — fires the
  // hydrate so the render doesn't get pinned to the wrong paidol.
  const hydrateAttemptedIdRef = useRef<string | null>(null);
  useEffect(() => {
    const mismatched = selectedPaidol && selectedPaidol.id !== selectedPaidolId;
    const needsHydrate = !selectedPaidol || mismatched;
    if (selectedPaidolId && needsHydrate && hydrateAttemptedIdRef.current !== selectedPaidolId) {
      hydrateAttemptedIdRef.current = selectedPaidolId;
      dispatch(hydrateSelectedPaidolIfNeeded(selectedPaidolId))
        .unwrap()
        .then((paidol) => {
          // Null response means the paidol was deleted, soft-deleted, or
          // the resolver denied access. Without recovery we'd sit in
          // RequireAuth's spinner forever because selectedPaidol stays
          // undefined and FeatureFlagProvider's effect (gated on
          // selectedPaidol.id) never fires. Bounce to the first member
          // paidol so the user can keep working instead of being trapped.
          if (!paidol && paidolUsers.length > 0) {
            // eslint-disable-next-line no-restricted-syntax -- SEE-1352: the selected paidol was deleted or denied, so scoping to it is exactly what failed. Any membership is a valid escape hatch.
            const fallbackPaidolId = paidolUsers[0]?.paidol?.id;
            if (fallbackPaidolId && fallbackPaidolId !== selectedPaidolId) {
              localStorage.removeItem(PAIDOL_SESSION_ID_KEY);
              dispatch(savePaidolSessionId(fallbackPaidolId));
            }
          }
        })
        .catch(() => {
          // Thunk rejection (network/auth error) — same recovery path.
          if (paidolUsers.length > 0) {
            // eslint-disable-next-line no-restricted-syntax -- SEE-1352: same recovery path as above; hydrating the selected paidol threw, so any membership is a valid escape hatch.
            const fallbackPaidolId = paidolUsers[0]?.paidol?.id;
            if (fallbackPaidolId && fallbackPaidolId !== selectedPaidolId) {
              localStorage.removeItem(PAIDOL_SESSION_ID_KEY);
              dispatch(savePaidolSessionId(fallbackPaidolId));
            }
          }
        });
    }
  }, [dispatch, selectedPaidolId, selectedPaidol, paidolUsers]);

  // Reset guards when user changes
  useEffect(() => {
    hasSetPaidolId.current = false;
    previousSelectedPaidolId.current = '';
    hasFetchedUserCompanies.current = null;
    hasCalledGetCurrentUser.current = false;
  }, [userSub]);

  // Only call getCurrentUser once on mount
  useEffect(() => {
    if (!hasCalledGetCurrentUser.current) {
      dispatch(getCurrentUser());
      hasCalledGetCurrentUser.current = true;
    }
    const refreshTokenInterval = setInterval(() => {
      dispatch(getCurrentUser());
    }, 10 * 60 * 1000);
    return () => {
      clearInterval(refreshTokenInterval);
    };
  }, []);

  // SEE-1200: an authenticated user with an SSO attempt still pending means
  // the attempt completed — the redirect (or the native shell's token
  // injection) landed us back with a session. Resolve it as a success so
  // every attempt gets a terminal log event.
  //
  // SEE-1350: before resolving, verify that the account Microsoft
  // authenticated matches what the user typed. A divergence sets
  // authSlice.ssoMismatch and SsoAccountVerificationGate renders the choice
  // screen. Unconditional — no feature flag to read, so nothing here waits on
  // remote config and every branch below fails open to today's behavior.
  useEffect(() => {
    if (!userSub) return;
    const pending = getPendingSsoAttempt();
    const switchMarker = readSsoSwitchMarker();
    if (!pending && !switchMarker) return;

    let active = true;
    (async () => {
      const typedEmail = pending?.email ?? switchMarker?.typedEmail;
      const provider = pending?.provider ?? switchMarker?.provider ?? 'unknown';
      let claims: SsoIdTokenClaims | null = null;
      try {
        const session = await Auth.currentSession();
        claims = session.getIdToken().payload as SsoIdTokenClaims;
      } catch {
        claims = null;
      }
      if (!active) return;

      const identifiers = extractSsoIdentifiers(claims);
      const authenticatedIdentity = authenticatedDisplayIdentity(identifiers);
      // Fail open: nothing typed (direct SSO entry), no readable claims, no
      // displayable identity, or a non-federated session (no identities claim
      // means a password login raced a stale marker) — behave exactly as today.
      const isFederated = Boolean(claims?.identities);
      if (!typedEmail || !authenticatedIdentity || !isFederated) {
        if (pending) resolveSsoAttempt('success');
        clearSsoSwitchMarker();
        return;
      }

      if (matchesTypedIdentifier(typedEmail, identifiers)) {
        if (pending) resolveSsoAttempt('success');
        clearSsoSwitchMarker();
        return;
      }

      // Divergence. The attempt itself completed — resolve its telemetry,
      // then surface the choice screen. `repeat` marks the web escalation:
      // a switch was attempted and the same wrong account answered again.
      if (pending) resolveSsoAttempt('success');
      const repeat =
        switchMarker?.phase === 'retried' &&
        switchMarker.expectIdentity?.toLowerCase() === authenticatedIdentity.toLowerCase();
      logSsoMismatch(repeat ? 'repeat' : 'shown', {
        typedEmail,
        authenticatedIdentity,
        provider,
      });
      dispatch(setSsoMismatch({ typedEmail, authenticatedIdentity, provider, repeat: Boolean(repeat) }));
    })();
    return () => {
      active = false;
    };
  }, [userSub, dispatch]);

  // Only fetch user profile and companies once per userSub
  useEffect(() => {
    if (userSub && hasFetchedUserCompanies.current !== userSub) {
      dispatch(getUserProfile(userSub));
      dispatch(getUserCompanies(userSub));
      dispatch(triggerBackupLocalStorage());
      hasFetchedUserCompanies.current = userSub;
    }
  }, [userSub, dispatch]);

  // Handle federated session after authentication (only when no session exists - SPE-2983)
  useEffect(() => {
    if (userSub && authState.user) {
      const federatedSessionId = localStorage.getItem(`${SESSION_ID_KEY}_federated`);
      const existingSessionId = localStorage.getItem(SESSION_ID_KEY);
      if (!federatedSessionId && !existingSessionId) {
        dispatch(handleFederatedSession());
      }
    }
  }, [userSub, authState.user, dispatch]);

  return <AuthContext.Provider value={{ authState, userCompaniesState }}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthState {
  return useContext(AuthContext).authState;
}

export function useUserCompanies(): UserCompaniesState {
  return useContext(AuthContext).userCompaniesState;
}

export function useUserHasRole(neededRole: Role): boolean | undefined {
  const logger = useLogger();
  const { userRole, isUserCompaniesStateUnknown } = useUserCompanies();

  if (isUserCompaniesStateUnknown || !userRole) {
    return undefined;
  }

  return userHasRole(neededRole, userRole, logger);
}

export function userHasRole(neededRole: Role, userRole: Role, logger: Logger) {
  switch (neededRole) {
    case Role.ADMINISTRATOR:
      return userRole === Role.ADMINISTRATOR;
    case Role.GROUP_ADMINISTRATOR:
      return userRole === Role.GROUP_ADMINISTRATOR || userRole === Role.ADMINISTRATOR;
    case Role.MEMBER:
      return userRole !== Role.CARDHOLDER;
    case Role.CARDHOLDER:
      return true;
    default:
      logger.error('Unrecognized user role', neededRole, 'assumed FALSE');
      return false; // This should never happen
  }
}

export default AuthProvider;
